import React from "react";
import classNames from "classnames";
import ReactCountriesFlags from "react-countries-flags";
import { LoadingIndicator } from "@jsq/ds/core";
import { FormSelect as Select } from "@jsq/ds/forms";
import { useGetCountryCodesQuery } from "#graphql/supergraphHooks";
import { clientContext } from "#utils/graphql_utils";
import { capitalizeEachWord } from "../../utils";
import { countryOption, countrySelection } from "./styles_vanilla.css";

export type Country = { code: string; name: string; id: string };
type CountryValue = keyof Country;

export type GetCountryValue = (country: Country) => Country[CountryValue];

export const defaultGetValue: GetCountryValue = (country) => country.code;

type Props = {
  name: string;
  className?: string;
  isFull?: boolean;
  placeholder?: string;
  handleChange?: (value: string | number | string[] | number[]) => void;
  getValue?: GetCountryValue;
  label?: string;
  disabled?: boolean;
  /**
   * Optional predicate to filter the country list. Receives the full country object
   * so callers can filter by any field (alpha2code, id, name, etc.).
   * e.g. filter by alpha2code: `(country) => isSepaCountry(country.code)`
   * e.g. filter by id allowlist: `(country) => allowedIds.includes(country.id)`
   */
  filterCountries?: (country: Country) => boolean;
  /**
   * Renders each option's flag beside its name. Opt-in so existing consumers
   * keep their current appearance.
   */
  showFlags?: boolean;
};

export const CountrySelectionDropDown: React.FunctionComponent<Props> = ({
  name,
  className = undefined,
  isFull = true,
  placeholder = "Select a country",
  handleChange = undefined,
  getValue = defaultGetValue,
  label = undefined,
  disabled = false,
  filterCountries = undefined,
  showFlags = false,
}) => {
  const { loading, data } = useGetCountryCodesQuery({
    context: clientContext(),
  });
  if (loading && !disabled) {
    return <LoadingIndicator />;
  }
  const countryList =
    data?.countries?.edges
      ?.filter((edge) => {
        if (!edge?.node?.alpha2code) return false;
        if (!filterCountries) return true;
        return filterCountries({
          code: edge.node.alpha2code,
          name: edge.node.name,
          id: edge.node.id,
        });
      })
      ?.map((edge) => {
        return { code: edge.node.alpha2code || "", name: edge.node.name, id: edge.node.id };
      })
      ?.sort((c1, c2) => (c1.name < c2.name ? -1 : 1)) || [];

  return (
    <Select
      name={name}
      full={isFull}
      placeholder={placeholder}
      className={classNames(countrySelection, className)}
      handleChange={handleChange}
      disabled={disabled}
      data-cy={`${name}-country`}
      data-testid={`${name}-country`}
      label={label}
    >
      <Select.Section>
        {countryList.map((country) => {
          const countryName =
            country.name === "U.S. Virgin Islands" // capitalizeEachWord turns U.S. Virgin Islands to U.s. Virgin Islands
              ? country.name
              : capitalizeEachWord(country.name);
          return (
            <Select.Option
              key={country.id}
              value={getValue(country)}
              contentClassName={showFlags ? countryOption : undefined}
            >
              {showFlags ? (
                <>
                  <ReactCountriesFlags
                    data-testid={`${name}-country-flag-${country.code}`}
                    isoCode={country.code}
                    alt={`${country.code} flag`}
                  />
                  <span>{countryName}</span>
                </>
              ) : (
                countryName
              )}
            </Select.Option>
          );
        })}
      </Select.Section>
    </Select>
  );
};
